import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route as ReactRoute } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { useDispatch, useSelector } from "react-redux";
import {
  CookieService,
  GoogleAnalyticsService,
  LocalStorageService,
} from "shared/services";
import { ILocalStorageKey, IUnitType } from "shared/types";

import { Toasts } from "shared/components/Toast";
import { MessageModal } from "shared/components/MessageModal";
import { CookieNotice, Head, Loader } from "shared/components";
import { getCurrentUser, currentUserSelector } from "Auth/state";
import { setUnitType } from "shared/components/UnitConverter/state";
import { VenuesRouter } from "Venues/router";
import { BandsRouter } from "Bands/router";
import { useGoogleAnalytics, isBandsDomain, isVenueDomain } from "shared/utils";
import { getImpersonateCookie } from "shared/utils/getImpersonateCookie";
import { copy } from "shared/assets";

const { calendar } = copy;

/*
  Router component is setup with Route-based code splitting.
  - Routes provided with the `privateRoute` prop are wrapped with the PrivateRoute component
  - Routes provided with the `authRoute` prop are wrapped with the AuthRoute component
*/
const Component: React.FC = () => {
  const [showCookieNotice, setShowCookieNotice] = useState(
    !CookieService.userHasSetCookiePreference()
  );
  const currentUser = useSelector(currentUserSelector);
  const dispatch = useDispatch();

  useGoogleAnalytics();

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  // get chosen unit type from localStorage
  const storedUnitType = LocalStorageService.getItem(
    ILocalStorageKey.unitType
  ) as IUnitType;

  // update unit type in app store
  useEffect(() => {
    if (storedUnitType) {
      dispatch(setUnitType(storedUnitType));
    }

    if (!!getImpersonateCookie()) {
      CookieService.acceptAllCookies();
      setShowCookieNotice(false);
    }
  }, [dispatch, storedUnitType]);

  return (
    <>
      <Head title={calendar.title} />
      {showCookieNotice && (
        <CookieNotice
          handleAcceptAll={() => {
            CookieService.acceptAllCookies();
            GoogleAnalyticsService.initialize(currentUser);
            GoogleAnalyticsService.pageView(
              `${window.location.pathname}${window.location.search}`
            );
            setShowCookieNotice(false);
          }}
          handleDenyAnalytical={() => {
            CookieService.denyAnalyticalCookies();
            setShowCookieNotice(false);
          }}
        />
      )}
      <QueryParamProvider ReactRouterRoute={ReactRoute}>
        <Toasts />
        <MessageModal />
        <Suspense fallback={<Loader />}>
          {isVenueDomain && (
            <VenuesRouter
              currentUser={currentUser}
              handleOpenCookies={() => setShowCookieNotice(true)}
            />
          )}
          {isBandsDomain && (
            <BandsRouter
              currentUser={currentUser}
              handleOpenCookies={() => setShowCookieNotice(true)}
            />
          )}
        </Suspense>
      </QueryParamProvider>
    </>
  );
};

export const Router = () => (
  <BrowserRouter>
    <Component />
  </BrowserRouter>
);
